import { useParams } from 'react-router-dom'
import {
  useGetFilterDeviceTypesQuery,
  useGetProjectMainSettingsQuery,
  usePostProjectUpdateMainSettingsMutation,
} from '../../../../app/api'
import { parseDomainList } from '../../../../shared/lib/helpers/ExtractHostname'
import { Controller, useForm } from 'react-hook-form'
import React, { useEffect, useState } from 'react'
import { Square } from '../../../../shared/components/utility/square/Square'
import { BpCard } from '../../../../shared/components/cards/card/BpCard'
import { FormControls } from '../../../../shared/components/forms/form-controls/FormControls'
import { BpInput } from '../../../../shared/components/forms/input/BpInput'
import { BpTextArea } from '../../../../shared/components/forms/textarea/BpTextArea'
import { BpCheckbox } from '../../../../shared/components/forms/checkbox/BpCheckbox'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useTranslation } from 'react-i18next'
import { IUpdateDevicesForm } from '../../../projects/model/types/UpdateDevicesForm.interface'
import { IUpdateBrandForm } from '../../../projects/model/types/UpdateBrandForm.interface'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import clsx from 'clsx'
import styles from './ProjectMainSettings.module.scss'
import { ProjectNotifications } from '../project-notifications/ProjectNotifications'

export const ProjectMainSettings = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { handleSendMessage } = useAiChat()
  const [loaders, setLoaders] = useState({
    brand: false,
    devices: false,
  })

  const {
    data: mainSettings,
    isFetching,
    isSuccess,
  } = useGetProjectMainSettingsQuery(
    { id: Number(id) },
    {
      selectFromResult: (result) => {
        const item = result.data?.data?.items?.[0]

        return {
          ...result,
          data: {
            ...item,
            brand_variants: item?.brand_variants?.join('\n'),
            domains: item?.domains?.join('\n'),
          },
        }
      },
      skip: !id,
    },
  )

  const [updateMainSettings] = usePostProjectUpdateMainSettingsMutation()
  const { data: devices, isFetching: devicesIsFetching } = useGetFilterDeviceTypesQuery()

  const {
    control: controlBrand,
    handleSubmit: handleSubmitBrand,
    register: registerBrand,
    formState: { isDirty: isDirtyBrand, isValid: isValidBrand, errors: errorsBrand },
    setValue: setValueBrand,
    setError: setErrorBrand,
    watch: watchBrand,
    reset: resetBrand,
  } = useForm<IUpdateBrandForm>({
    mode: 'onChange',
    defaultValues: { brand_name: '', brand_variants: '', domains: '' },
  })

  const {
    control: controlDevices,
    handleSubmit: handleSubmitDevices,
    formState: { isDirty: isDirtyDevices, isValid: isValidDevices },
    watch: watchDevices,
    reset: resetDevices,
  } = useForm<IUpdateDevicesForm>({
    mode: 'onChange',
    defaultValues: { devices: [] },
  })

  const formBrand = watchBrand()
  const formDevices = watchDevices()

  useEffect(() => {
    resetBrand({
      brand_name: mainSettings.brand_name,
      brand_variants: mainSettings.brand_variants,
      domains: mainSettings.domains,
    })
  }, [isFetching])

  useEffect(() => {
    resetDevices({
      devices: devices?.map(
        (device) =>
          mainSettings.devices?.some((d: { id?: number }) => d.id === Number(device.value)) ||
          false,
      ),
    })
  }, [isFetching, devicesIsFetching])

  const domainsValidate = (list: string): string[] => {
    const domainRegex = /^(?:https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/

    return list
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line && !domainRegex.test(line))
  }

  const submitBrand = (values: IUpdateBrandForm) => {
    setLoaders({ ...loaders, brand: true })
    updateMainSettings({
      body: {
        scenario: 'update_brand',
        id: Number(id),
        brand_name: values.brand_name,
        brand_variants: values.brand_variants.split('\n'),
        domains: parseDomainList(values.domains),
      },
    })
  }

  const submitDevices = (values: IUpdateDevicesForm) => {
    setLoaders({ ...loaders, devices: true })
    updateMainSettings({
      body: {
        scenario: 'update_devices',
        id: Number(id),
        devices: devices
          ?.filter((_, index) => values?.devices[index])
          .map((device) => ({ id: Number(device.value) })),
      },
    })
  }

  useEffect(() => {
    if (!isFetching && isSuccess)
      setLoaders({
        brand: false,
        devices: false,
      })
  }, [isFetching])

  if (isFetching && !isSuccess) {
    return <Preloader />
  }

  return (
    <section className={styles.root}>
      <Square className={styles.square}>
        <div className={clsx(loaders.brand ? styles.loaderVisible : styles.loaderInvisible)}>
          <Preloader />
        </div>
        <form
          className={clsx(loaders.brand ? styles.formInvisible : styles.formVisible)}
          onSubmit={handleSubmitBrand(submitBrand)}
        >
          <BpCard
            footer={
              <FormControls
                disabled={
                  !isDirtyBrand ||
                  !isValidBrand ||
                  !formBrand.brand_name.length ||
                  !!domainsValidate(formBrand?.domains).length
                }
                canceled={isDirtyBrand}
                onCancel={() => resetBrand()}
              />
            }
            description={t('pages.project.brandDescription')}
            size={'sm'}
            title={t('pages.project.brand')}
          >
            <div className={styles.cardGap}>
              <Controller
                render={({ field }) => (
                  <BpInput
                    {...field}
                    required
                    error={errorsBrand.brand_name?.message as string}
                    label={t('fields.brandName')}
                    placeholder={t('pages.project.placeholders.brandName')}
                    onClear={() => setValueBrand('brand_name', '', { shouldValidate: true })}
                    {...registerBrand('brand_name', {
                      required: t('fields.validate.required'),
                      maxLength: {
                        value: 100,
                        message: t('fields.validate.under100Characters'),
                      },
                    })}
                  />
                )}
                control={controlBrand}
                name='brand_name'
              />
              <Controller
                render={({ field }) => (
                  <BpTextArea
                    {...field}
                    required
                    rightSection={
                      <AiButton
                        size={'sm'}
                        variant={'outlined'}
                        onClick={() =>
                          handleSendMessage({
                            message: `Generate typos for my brand ${mainSettings.brand_name}`,
                          })
                        }
                      >
                        {t('buttons.createWithAI')}
                      </AiButton>
                    }
                    error={errorsBrand.brand_variants?.message as string}
                    label={t('fields.brandsMisspellingsVariations')}
                    placeholder={t('pages.project.placeholders.brandsMisspellingsVariations')}
                    {...registerBrand('brand_variants', {
                      required: t('fields.validate.required'),
                      pattern: /.*\S+.*/,
                      onChange: (e) => {
                        setValueBrand('brand_variants', e.target.value.replace(',', '\n'))
                      },
                      onBlur: (e) => {
                        setValueBrand(
                          'brand_variants',
                          e.target.value
                            .split('\n')
                            .map((value: string) => value.trim())
                            .join('\n'),
                        )
                      },
                    })}
                  />
                )}
                control={controlBrand}
                name='brand_variants'
              />
              <Controller
                render={({ field }) => (
                  <BpTextArea
                    {...field}
                    required
                    error={errorsBrand.domains?.message as string}
                    info={t('pages.projects.info.domains')}
                    label={t('fields.domains')}
                    placeholder={t('pages.project.placeholders.domains')}
                    {...registerBrand('domains', {
                      required: t('fields.validate.required'),
                      pattern: /.*\S+.*/,
                      onChange: (e) => {
                        setValueBrand(
                          'domains',
                          e.target.value
                            .replace(',', '\n')
                            .replace(/[^a-zA-Zа-яА-Я0-9\s.,-:/]/g, ''),
                        )
                      },
                      onBlur: (e) => {
                        if (domainsValidate(e.target.value).length > 0) {
                          setErrorBrand('domains', { message: t('fields.validate.domain') })
                        }
                      },
                    })}
                  />
                )}
                control={controlBrand}
                name='domains'
              />
            </div>
          </BpCard>
        </form>
      </Square>
      <Square className={styles.square}>
        <div className={clsx(loaders.devices ? styles.loaderVisible : styles.loaderInvisible)}>
          <Preloader />
        </div>
        <form
          className={clsx(loaders.devices ? styles.formInvisible : styles.formVisible)}
          onSubmit={handleSubmitDevices(submitDevices)}
        >
          <BpCard
            footer={
              <FormControls
                disabled={
                  !isDirtyDevices || !isValidDevices || !formDevices?.devices.includes(true)
                }
                canceled={isDirtyDevices}
                onCancel={() => resetDevices()}
              />
            }
            description={t('pages.project.devicesDescription')}
            size={'sm'}
            title={t('pages.project.devices')}
          >
            <div>
              <div className={styles.devicesLabel}>
                {t('fields.deviceTypes')}
                <span> *</span>
              </div>
              <div className={styles.devicesList}>
                {devices?.map((device, index) => {
                  const fieldName = `devices.${index}` as const

                  return (
                    <Controller
                      key={index}
                      render={({ field: { value, ...fieldProps } }) => (
                        <BpCheckbox
                          {...fieldProps}
                          checked={value}
                          defaultChecked={false}
                          label={t('fields.options.' + device.text)}
                        />
                      )}
                      control={controlDevices}
                      name={fieldName}
                    />
                  )
                }) || (
                  <div className={styles.preloaderBox}>
                    <Preloader />
                  </div>
                )}
              </div>
            </div>
          </BpCard>
        </form>
      </Square>
      <Square className={styles.square}>
        <ProjectNotifications />
      </Square>
    </section>
  )
}
